import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PullToRefresh from "@/components/PullToRefresh";
import CompBanner from "@/components/CompBanner";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { usePublicPrices } from "@/lib/usePublicPrices";
import { track } from "@/lib/analytics";
import { setPostAuthRedirect } from "@/lib/post-auth-redirect";
import { useAuth } from "@/contexts/AuthContext";
import InquireButton from "@/components/InquireButton";
import BuyNowButton from "@/components/BuyNowButton";
import VerifiedBadge from "@/components/VerifiedBadge";
import VerifiedStandardsBadge from "@/components/VerifiedStandardsBadge";
import EliteBadge from "@/components/EliteBadge";
import MakeOfferButton from "@/components/MakeOfferButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import WatchButton from "@/components/WatchButton";
import HoverVideoPreview from "@/components/HoverVideoPreview";
import EmptyState from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/SkeletonList";
import { BuyerNoticePill } from "@/components/BuyerNoticeSheet";
import { MagnifyingGlassIcon as MagnifyingGlass, SparkleIcon as Sparkle, XIcon as CloseIcon, PawPrintIcon as PawPrint, StorefrontIcon as Storefront, GenderMaleIcon as GenderMale, GenderFemaleIcon as GenderFemale, CompassIcon as Compass, StarIcon as Star, VideoCameraIcon as VideoCamera, ArrowRightIcon as ArrowRight, PlusIcon as Plus, BellRingingIcon as BellRinging, GlobeHemisphereWestIcon as GlobeHemisphereWest, ShareNetworkIcon as ShareNetwork } from "@phosphor-icons/react";
import { thumbUrl, formatDate, formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";
import { LuxuryCard, WaxSeal } from "@/components/luxury";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckIcon as Check } from "@phosphor-icons/react";
import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import { useUserPlanTier } from "@/hooks/useUserPlanTier";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";

/** Marketplace — buyers browse purebreds & exotic breeds for sale. */

const SORTS = [
  { value: "newest", label: "Newest first" },
  { value: "price_asc", label: "Price: low → high" },
  { value: "price_desc", label: "Price: high → low" },
];

const SEX_OPTIONS = [
  { value: "any", label: "Any sex" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

/** Country options for the marketplace "Ships from" selector.
 *  Only includes countries we actively expect kennels in — keeps the
 *  dropdown short and meaningful. `any` is the default. */
const SHIPS_FROM_OPTIONS = [
  { value: "any", label: "Any country" },
  { value: "US",  label: "United States" },
  { value: "CA",  label: "Canada" },
  { value: "MX",  label: "Mexico" },
  { value: "GB",  label: "United Kingdom" },
  { value: "IE",  label: "Ireland" },
  { value: "FR",  label: "France" },
  { value: "DE",  label: "Germany" },
  { value: "NL",  label: "Netherlands" },
  { value: "ES",  label: "Spain" },
  { value: "IT",  label: "Italy" },
  { value: "PL",  label: "Poland" },
  { value: "RO",  label: "Romania" },
  { value: "RU",  label: "Russia" },
  { value: "JP",  label: "Japan" },
  { value: "KR",  label: "South Korea" },
  { value: "AU",  label: "Australia" },
  { value: "NZ",  label: "New Zealand" },
];

/** Age bands surfaced as quick-filter chips. Counts come from
 *  /marketplace/stats so we don't show empty bands. */
const AGE_BANDS = [
  { value: "any",    label: "All ages" },
  { value: "puppy",  label: "Puppy",  hint: "Under 6 mo" },
  { value: "junior", label: "Junior", hint: "6–18 mo" },
  { value: "adult",  label: "Adult",  hint: "1.5–7 yr" },
  { value: "senior", label: "Senior", hint: "7 yr+" },
];

function priceLabel(amount, currency = "USD") {
  if (!amount || amount <= 0) return "Inquire";
  // `style: "currency"` defaults `minimumFractionDigits` to the
  // currency's typical digits (2 for USD). Setting `max: 0` alone
  // makes min(2) > max(0) → RangeError. Pin both.
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function Marketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalog, setCatalog] = useState([]);
  const [breeders, setBreeders] = useState([]);
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [q, setQ] = useState("");
  // Pre-seed from `?breed=...` so a user landing here from the
  // landing-page hero search (or post-signup intent redirect) sees
  // their breed already filtered. Stripped from URL after consumption
  // so back-nav doesn't re-add it.
  const [selectedBreeds, setSelectedBreeds] = useState(() => {
    const initial = (searchParams.get("breed") || "").trim();
    return initial ? [initial] : [];
  });
  useEffect(() => {
    if (searchParams.get("breed")) {
      const next = new URLSearchParams(searchParams);
      next.delete("breed");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [exoticOnly, setExoticOnly] = useState(false);
  const [sex, setSex] = useState("any");
  const [age, setAge] = useState("any");
  const [breederId, setBreederId] = useState("any");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sort, setSort] = useState("newest");
  // Cross-border filters — added 2026-02 alongside the Inbound Intl
  // Breeder Directory + WTB board. Country selector defaults to
  // "any" so US-domestic browsers don't see a filtered set until
  // they explicitly opt in.
  const [shipsFrom, setShipsFrom] = useState("any");
  const [intlShippingOnly, setIntlShippingOnly] = useState(false);
  const [breedingRightsOnly, setBreedingRightsOnly] = useState(false);
  // List-your-dog dialog state
  const [listOpen, setListOpen] = useState(false);
  const [myDogs, setMyDogs] = useState(null);
  const [pickedDogId, setPickedDogId] = useState(null);
  // Bumped after a successful in-place listing so the grid + stats
  // re-fetch without forcing the user to refresh the page.
  const [refreshTick, setRefreshTick] = useState(0);

  // One-time fetches — `/breeds/catalog` and `/marketplace/stats`
  // are public; `/breeders` is auth-only so anonymous visitors get an
  // empty list there (the breeder filter just won't show until login).
  useEffect(() => {
    api.get("/breeds/catalog").then((r) => setCatalog(r.data?.breeds || [])).catch(() => {});
    api.get("/breeders").then((r) => setBreeders(r.data || [])).catch(() => setBreeders([]));
    // Fire a single impression event so we can chart anonymous-vs-authed
    // marketplace views in PostHog. We deliberately don't include any
    // PII — the user's id (if any) is already attached upstream by
    // analytics.identify().
    track("marketplace_viewed", { authed: !!user });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stats refetch — runs on mount AND after a successful in-place
  // listing so the "Available · Breeds · Exotic" tiles stay accurate.
  useEffect(() => {
    api.get("/marketplace/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [refreshTick]);

  // Debounced fetch on filter change
  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      selectedBreeds.forEach((b) => params.append("breeds", b));
      if (exoticOnly) params.set("exotic_only", "true");
      if (sex !== "any") params.set("sex", sex);
      if (age !== "any") params.set("age", age);
      if (breederId !== "any") params.set("breeder_id", breederId);
      if (minPrice) params.set("min_price", String(Number(minPrice) || 0));
      if (maxPrice) params.set("max_price", String(Number(maxPrice) || 0));
      if (shipsFrom !== "any") params.set("seller_country", shipsFrom);
      if (intlShippingOnly) params.set("intl_shipping", "true");
      if (breedingRightsOnly) params.set("breeding_rights", "true");
      params.set("sort", sort);
      setLoading(true);
      api
        .get(`/marketplace/listings?${params.toString()}`)
        .then((r) => setListings(r.data?.listings || []))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, selectedBreeds, exoticOnly, sex, age, breederId, minPrice, maxPrice, shipsFrom, intlShippingOnly, breedingRightsOnly, sort, refreshTick]);

  const breedSearchSuggestions = useMemo(() => {
    if (!q.trim()) return [];
    const lower = q.toLowerCase();
    return catalog
      .filter(
        (b) =>
          b.name.toLowerCase().includes(lower) &&
          !selectedBreeds.find((s) => s.toLowerCase() === b.name.toLowerCase())
      )
      .slice(0, 5);
  }, [q, catalog, selectedBreeds]);

  const addBreed = (name) => {
    setSelectedBreeds((prev) => [...prev, name]);
    setQ("");
  };
  const removeBreed = (name) =>
    setSelectedBreeds((prev) => prev.filter((b) => b !== name));

  const reset = () => {
    setQ("");
    setSelectedBreeds([]);
    setExoticOnly(false);
    setSex("any");
    setAge("any");
    setBreederId("any");
    setMinPrice("");
    setMaxPrice("");
    setShipsFrom("any");
    setIntlShippingOnly(false);
    setBreedingRightsOnly(false);
    setSort("newest");
  };

  /** Open the "list your dog" picker. Fetches the caller's own dogs
   *  on demand so we don't load them for users who never click List.
   *  Empty result → redirect to /dogs/new so user can register first. */
  const openListDialog = async () => {
    if (!user) { navigate("/login?intent=list-dog"); return; }
    setListOpen(true);
    if (myDogs === null) {
      try {
        const r = await api.get("/dogs");
        setMyDogs(r.data || []);
      } catch {
        setMyDogs([]);
      }
    }
  };

  // Anonymous visitors get the marketing shell (with public-nav header
  // pointing to /login + /register); authed visitors get the full AppShell
  // with the kennel sidebar etc. The marketplace UI itself is identical.
  const Shell = user ? AppShell : PublicShell;  return (
    <Shell>
      <PullToRefresh
        onRefresh={() => new Promise((resolve) => {
          setRefreshTick((t) => t + 1);
          // Hold the spinner ~1.2s — long enough that the fetch usually
          // completes underneath. The listings array updates on its own
          // once the request lands.
          setTimeout(resolve, 1200);
        })}
        testId="marketplace-ptr"
      >
      <SeoHead
        title="Marketplace · Pedigree-Verified Dogs for Sale · Armstrong K9 Registry"
        description="Browse pedigree-verified dogs for sale from trusted breeders. Filter by breed, sex, age, and price. Every listing includes registration history, health files, and verified ownership."
        url={`${webOrigin()}/marketplace`}
        type="website"
        breadcrumbs={[
          { name: "Home", url: `${webOrigin()}/` },
          { name: "Marketplace", url: `${webOrigin()}/marketplace` },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Dog Marketplace · Armstrong K9 Registry",
          "description": "Pedigree-verified dogs for sale from trusted breeders.",
          "url": `${webOrigin()}/marketplace`,
        }}
      />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">
              Marketplace
            </p>
            <BuyerNoticePill />
          </div>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Find your dog
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
            Purebreds and exotic breeds listed by registered kennels and
            owners — puppies, juniors, proven adults, and retired studs all
            welcome. Filter by breed, age, breeder, sex, or price.
          </p>
          <CompBanner className="mt-4 max-w-2xl" />
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            onClick={openListDialog}
            className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-5"
            data-testid="list-your-dog-btn"
          >
            <Plus size={14} className="mr-1.5" weight="bold" />
            List your dog
          </Button>
          <Link to="/breeds">
            <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11" data-testid="browse-breeds-btn">
              <Compass size={14} className="mr-1.5" weight="duotone" /> Browse all breeds
            </Button>
          </Link>
          <Link to="/marketplace/upcoming">
            <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11" data-testid="browse-upcoming-btn">
              Upcoming litters
            </Button>
          </Link>
          <Link to="/marketplace/wtb">
            <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11" data-testid="browse-wtb-btn">
              <Compass size={14} className="mr-1.5" weight="duotone" /> WTB Board
            </Button>
          </Link>
          <Link to="/intl-breeders">
            <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11" data-testid="browse-intl-breeders-btn">
              <GlobeHemisphereWest size={14} className="mr-1.5" weight="duotone" /> Intl breeders
            </Button>
          </Link>
        </div>
      </div>

      {/* Cross-sell to intl couriers — buyers browsing the marketplace
          may be looking abroad for breeds rare in the US. Surfaces the
          intl directory and turns marketplace traffic into intl-courier
          leads without disrupting the dog-buying flow. */}
      <Link
        to="/transport/international"
        className="flex items-center gap-3 mb-6 rounded-xl border border-[#E8E0CC] bg-cream px-4 py-3 hover:border-gold transition-colors group"
        data-testid="marketplace-intl-courier-crosssell"
      >
        <span className="text-lg">✈️</span>
        <div className="flex-1 text-[13px]">
          <span className="font-semibold text-ink">Buying from abroad?</span>
          <span className="text-[#6B6B6B] ml-1.5">Find verified intl couriers for cross-border transport.</span>
        </div>
        <span className="text-[12px] font-semibold text-[#C1272D] group-hover:text-ink">See couriers →</span>
      </Link>

      {/* Age band quick filters — discoverability for buyers looking for
          adults / seniors as well as the usual puppy seekers. */}
      <div className="flex flex-wrap gap-2 items-center mb-6" data-testid="age-filter-row">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mr-1">Looking for:</span>
        {AGE_BANDS.map((b) => {
          const isActive = age === b.value;
          const n = b.value === "any"
            ? (stats?.available ?? null)
            : (stats?.age_counts?.[b.value] ?? null);
          return (
            <button
              key={b.value}
              type="button"
              onClick={() => setAge(b.value)}
              data-testid={`age-chip-${b.value}`}
              className={`rounded-full px-3 py-1.5 text-[12px] border transition-colors ${
                isActive
                  ? "bg-ink text-white border-ink"
                  : "border-[#E5E5E5] text-[#6B6B6B] hover:border-ink hover:text-ink"
              }`}
            >
              {b.label}
              {b.hint && (
                <span className={`ml-1 text-[10px] ${isActive ? "opacity-70" : "text-[#9B9B9B]"}`}>
                  {b.hint}
                </span>
              )}
              {n !== null && n !== undefined && (
                <span className={`ml-1.5 font-mono ${isActive ? "opacity-70" : "text-[#9B9B9B]"}`}>
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Stats strip — labels chosen so the three counts read as
          related-but-not-mutually-exclusive: "Available" is the total,
          "Breeds available" is how many unique breeds those listings
          cover, and "Exotic breeds" is the subset that's flagged exotic
          in the catalog. A single exotic-breed listing therefore shows
          1 / 1 / 1, which is correct and no longer reads as a
          double-count. */}
      {stats && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8 max-w-3xl">
          <Stat icon={PawPrint} label="Available listings" value={stats.available} />
          <Stat icon={Compass} label="Breeds available" value={stats.distinct_breeds} hint="Unique breeds (incl. exotic)" />
          <Stat icon={Sparkle} label="Of those, exotic" value={stats.exotic_listings} tone="#C9A227" hint="Subset, not separate" />
        </div>
      )}

      {/* Video Tours promo strip — anchored above the filter row so it's
          unmissable for any buyer scanning listings. Dismissible via
          localStorage so we don't nag the same user every page load. */}
      <VideoTourPromoStrip />

      {/* "Adult dogs for sale" sub-shelf — signals to buyers that the
          marketplace welcomes adult sales (retired studs, proven adults,
          etc.), not just puppies. Hidden when fewer than 3 adult
          listings exist so we never render a hollow shelf. */}
      <AdultDogsShelf currentUserId={user?.id} onJumpToAdults={() => setAge("adult")} />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
        {/* Filters */}
        <aside className="bg-white border border-[#E5E5E5] rounded-xl p-5 space-y-5 sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain" data-testid="market-filter-sidebar">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#3F3F3F]">
              Search
            </Label>
            <div className="relative mt-2">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Type a breed, color, or keyword"
                data-testid="market-search-input"
              />
              {breedSearchSuggestions.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-[#E5E5E5] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {breedSearchSuggestions.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => addBreed(b.name)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#FBF7EC] flex items-center justify-between"
                      data-testid={`market-suggest-${b.name.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <span>{b.name}</span>
                      {b.exotic && (
                        <span className="text-[9px] uppercase tracking-[0.18em] font-bold bg-[#C9A227]/15 text-[#8a6f10] px-1.5 py-0.5 rounded">
                          Exotic
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedBreeds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selectedBreeds.map((b) => (
                  <span
                    key={b}
                    className="inline-flex items-center gap-1 bg-[#FBF7EC] border border-[#E8D9A6] rounded-full pl-2 pr-1 py-0.5 text-[12px]"
                    data-testid={`market-breed-chip-${b.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {b}
                    <button
                      type="button"
                      onClick={() => removeBreed(b)}
                      className="h-4 w-4 rounded-full hover:bg-[#E8D9A6] flex items-center justify-center"
                    >
                      <CloseIcon size={9} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#3F3F3F]">
              Ships from
            </Label>
            <Select value={shipsFrom} onValueChange={setShipsFrom}>
              <SelectTrigger className="mt-2" data-testid="market-ships-from">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72 overflow-y-auto">
                {SHIPS_FROM_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {shipsFrom !== "any" && shipsFrom !== "US" && (
              <Link
                to={`/transport/route/${shipsFrom.toLowerCase()}/us`}
                className="block mt-2 text-[11px] text-[#8A6A00] hover:text-crimson font-bold underline"
                data-testid="market-intl-route-cross-sell"
              >
                Find a courier from {shipsFrom} → US →
              </Link>
            )}
          </div>

          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={intlShippingOnly}
              onChange={(e) => setIntlShippingOnly(e.target.checked)}
              className="mt-0.5 accent-[#C9A227]"
              data-testid="market-intl-shipping-only"
            />
            <span>
              <span className="font-semibold">Intl shipping supported</span>
              <span className="block text-[11px] text-[#6B6B6B]">
                Seller is willing to ship internationally (you arrange courier).
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={breedingRightsOnly}
              onChange={(e) => setBreedingRightsOnly(e.target.checked)}
              className="mt-0.5 accent-[#C9A227]"
              data-testid="market-breeding-rights-only"
            />
            <span>
              <span className="font-semibold">Sold with breeding rights</span>
              <span className="block text-[11px] text-[#6B6B6B]">
                Full breeding rights included, not limited / pet-only.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={exoticOnly}
              onChange={(e) => setExoticOnly(e.target.checked)}
              className="mt-0.5 accent-[#C9A227]"
              data-testid="market-exotic-only"
            />
            <span>
              <span className="font-semibold">Exotic breeds only</span>
              <span className="block text-[11px] text-[#6B6B6B]">
                Surface rarer, less-common breeds you might not have considered.
              </span>
            </span>
          </label>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#3F3F3F]">
              Sex
            </Label>
            <Select value={sex} onValueChange={setSex}>
              <SelectTrigger className="mt-2" data-testid="market-sex">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEX_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#3F3F3F]">
              Breeder
            </Label>
            <Select value={breederId} onValueChange={setBreederId}>
              <SelectTrigger className="mt-2" data-testid="market-breeder">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any kennel</SelectItem>
                {breeders.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.kennel_name || b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#3F3F3F]">
              Price range
            </Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Input
                type="number"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="Min"
                data-testid="market-min-price"
              />
              <Input
                type="number"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="Max"
                data-testid="market-max-price"
              />
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#3F3F3F]">
              Sort
            </Label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="mt-2" data-testid="market-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            onClick={reset}
            className="w-full rounded-full border-[#E5E5E5]"
            data-testid="market-reset"
          >
            Reset filters
          </Button>

          {/* Save this search — buyer can save the active filter set and
              we'll email them daily when new listings match. Only shows
              when (a) the user is signed in and (b) at least one filter
              is set, so we never offer "Save: all listings" as a row. */}
          <SaveSearchInline
            user={user}
            filters={{
              breed: selectedBreeds[0] || "",
              sex: sex !== "any" ? sex : "",
              min_price: minPrice ? Number(minPrice) : null,
              max_price: maxPrice ? Number(maxPrice) : null,
            }}
          />
        </aside>

        {/* Results */}
        <section data-testid="market-results">
          {loading && <CardGridSkeleton count={6} testId="market-loading-skeleton" />}
          {!loading && listings.length === 0 && (
            <EmptyState
              testId="market-empty"
              icon={<MagnifyingGlass size={32} weight="duotone" />}
              title="No listings match these filters"
              description={isNativeApp()
                ? "Try clearing a filter — or set a breed alert so we email you the moment a matching litter is registered."
                : "Try clearing a filter — or set a Buyer Pass breed alert so we email you the moment a matching litter is registered."}
              ctaLabel="Clear filters"
              ctaOnClick={() => { setSelectedBreeds([]); setSex("any"); setMinPrice(""); setMaxPrice(""); setExoticOnly(false); setQ(""); setBreederId("any"); }}
              secondaryLabel="Set up alerts"
              secondaryTo="/settings"
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {listings.map((l) => (
              <ListingCard key={l.id} listing={l} currentUserId={user?.id} />
            ))}
          </div>
        </section>
      </div>
      <ListYourDogDialog
        open={listOpen}
        onOpenChange={setListOpen}
        myDogs={myDogs}
        pickedDogId={pickedDogId}
        setPickedDogId={setPickedDogId}
        onListed={() => {
          // Re-fetch listings + stats and refresh the cached "my dogs"
          // list so the just-listed dog drops out of the eligibility set.
          setRefreshTick((t) => t + 1);
          setMyDogs(null);
          setPickedDogId(null);
        }}
      />
      </PullToRefresh>
    </Shell>
  );
}

/** Picker dialog for the marketplace "List your dog" CTA. Lazily
 *  fetches the user's dogs, then completes the listing IN-PLACE by
 *  hitting `/billing/checkout` directly — no detour through the dog
 *  profile page. If the user's plan includes free listings, the dog
 *  is listed immediately and the marketplace refreshes. Otherwise we
 *  redirect to Stripe Checkout, and the BillingReturn page brings the
 *  user back here on success. */
function ListYourDogDialog({ open, onOpenChange, myDogs, pickedDogId, setPickedDogId, onListed }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const { isProOrElite } = useUserPlanTier();
  const iosBlocked = isIOS() && !isProOrElite;
  const eligible = (myDogs || []).filter((d) => !d.for_sale);
  const goCreate = () => { onOpenChange(false); navigate("/dogs/new"); };
  const goList = async () => {
    if (!pickedDogId || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "dog_marketplace_listing",
        origin_url: webOrigin(),
        dog_id: pickedDogId,
      });
      if (data.free) {
        toast.success(
          data.reason?.startsWith("included_with")
            ? "Listed — included with your plan."
            : "Listed on the marketplace for 60 days.",
        );
        onOpenChange(false);
        onListed?.();
      } else if (data.url) {
        // Stripe handles the rest; BillingReturn brings the user back.
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>List one of your dogs</DialogTitle>
          <DialogDescription>
            Pick a registered dog to put on the marketplace. The dog must be
            in our registry first — if it's not, register it now.
          </DialogDescription>
        </DialogHeader>
        {myDogs === null && (
          <p className="text-sm text-[#6B6B6B]">Loading your dogs…</p>
        )}
        {myDogs !== null && eligible.length === 0 && (
          <div className="text-sm bg-[#FAFAFA] border border-[#EAEAEA] rounded-lg p-4" data-testid="list-no-dogs">
            <p className="mb-2">
              {(myDogs || []).length === 0
                ? "You don't have any registered dogs yet."
                : "All of your registered dogs are already listed."}
            </p>
            {(myDogs || []).length === 0 && (
              <Button onClick={goCreate} className="rounded-full bg-ink hover:bg-crimson text-white" data-testid="list-go-register">
                <Plus size={14} className="mr-1.5" weight="bold" /> Register a dog
              </Button>
            )}
          </div>
        )}
        {myDogs !== null && eligible.length > 0 && (
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="list-dog-picker">
            {eligible.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setPickedDogId(d.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                  pickedDogId === d.id
                    ? "border-ink bg-ink/5"
                    : "border-[#EAEAEA] hover:border-ink"
                }`}
                data-testid={`list-dog-option-${d.id}`}
              >
                <div className="w-10 h-10 rounded bg-[#FAFAFA] flex-shrink-0 overflow-hidden flex items-center justify-center">
                  {d.photos?.[0] ? (
                    <img src={thumbUrl(d.photos[0])} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <PawPrint size={18} className="text-[#B8B8B8]" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{d.name}</p>
                  <p className="text-[11px] text-[#6B6B6B]">
                    {d.breed} · {d.sex} {d.dob ? `· born ${formatDate(d.dob)}` : ""}
                  </p>
                </div>
                {pickedDogId === d.id && <Check size={16} className="text-ink" weight="bold" />}
              </button>
            ))}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full" disabled={submitting}>
            Cancel
          </Button>
          {eligible.length > 0 && iosBlocked && (
            <IosPurchaseNotice label="Marketplace listing" className="w-full" />
          )}
          {eligible.length > 0 && !iosBlocked && (
            <Button
              onClick={goList}
              disabled={!pickedDogId || submitting}
              className="rounded-full bg-ink hover:bg-crimson text-white"
              data-testid="list-continue-btn"
            >
              {submitting ? "Listing…" : "List on marketplace"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon: Icon, label, value, tone = "#0E0E0E", hint = null }) {
  return (
    <LuxuryCard className="p-3 sm:p-4 min-w-0">
      <div className="flex items-center gap-2 mb-2 min-w-0">
        <div className="p-1.5 rounded-md flex-shrink-0" style={{ background: `${tone}15` }}>
          <Icon size={16} weight="duotone" color={tone} />
        </div>
        <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.18em] sm:tracking-[0.22em] text-[#6B6B6B] line-clamp-2 leading-tight">
          {label}
        </span>
      </div>
      <div className="text-xl sm:text-2xl md:text-3xl font-spectral font-light tracking-tight font-tabular leading-none">{value}</div>
      {hint && (
        <div className="text-[10px] text-[#9A9A9A] mt-1.5 italic leading-tight hidden sm:block">{hint}</div>
      )}
    </LuxuryCard>
  );
}

function ListingCard({ listing, currentUserId }) {
  const SexIcon = listing.sex === "female" ? GenderFemale : GenderMale;
  const photo = listing.photos?.[0];
  const isFeatured = !!listing.is_featured;
  const isVerifiedSeller = !!(listing.elite_verified || listing.seller_plan === "verified" || listing.verified_standards);
  const shareListing = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // OG-rich backend URL — crawlers get the per-listing 1200×630
    // card (puppy photo + name + price); humans redirect to the SPA.
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/dogs/${listing.id}/share`;
    try {
      if (navigator.share) {
        await navigator.share({ title: listing.name, url });
        return;
      }
    } catch { /* fall through */ }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Listing link copied — photo + price preview on social.");
    } catch {
      toast.error("Could not copy link");
    }
  };
  return (
    <div
      className={`relative bg-white rounded-xl overflow-hidden flex flex-col shadow-luxury-light hover:-translate-y-0.5 hover:shadow-gold-glow active:scale-[0.99] transition-all duration-500 border ${
        isFeatured
          ? "border-gold ring-1 ring-gold/40"
          : "border-[#EAEAEA] hover:border-gold/55"
      }`}
      data-testid={`listing-${listing.id}`}
    >
      {isFeatured && (
        <div
          className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-ink text-gold-light text-[10px] font-bold tracking-[0.2em] uppercase shadow-[0_4px_12px_rgba(0,0,0,0.4)]"
          data-testid={`listing-featured-${listing.id}`}
        >
          <Star size={11} weight="fill" /> Featured
        </div>
      )}
      {isVerifiedSeller && !isFeatured && (
        <div className="absolute top-3 left-3 z-10" title="Verified kennel">
          <WaxSeal size={28} title="Verified kennel" />
        </div>
      )}
      <div className="absolute top-3 right-3 z-10 flex flex-col items-center gap-2">
        <WatchButton targetType="breeder" targetId={listing.breeder?.id || ""} size={16} />
        <button
          type="button"
          onClick={shareListing}
          title="Share this listing"
          aria-label={`Share ${listing.name}`}
          data-testid={`listing-share-${listing.id}`}
          className="inline-flex items-center justify-center rounded-full border transition-colors h-9 w-9 bg-white border-[#E5E5E5] text-[#6B6B6B] hover:text-[#C9A227] hover:border-[#C9A227]/40"
        >
          <ShareNetwork size={16} weight="duotone" />
        </button>
      </div>
      <Link to={`/dogs/${listing.id}`} className="block">
        <div className="aspect-[4/3] bg-cream border-b border-[#E2DAC2] flex items-center justify-center overflow-hidden">
          <HoverVideoPreview
            photoUrl={photo ? thumbUrl(photo) : null}
            videos={listing.videos}
            alt={listing.name}
            className="w-full h-full"
            imgClassName="photo-luxury"
          />
        </div>
        <div className="p-5 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <h3 className="font-spectral text-xl tracking-tight truncate flex-1">{listing.name}</h3>
            <SexIcon size={16} weight="duotone" className={listing.sex === "female" ? "text-crimson" : "text-ink"} />
          </div>
          <div className="text-sm text-[#3F3F3F]">{listing.breed}</div>
          {listing.seller_country && listing.seller_country !== "US" && (
            <span
              className="inline-flex items-center gap-1 w-fit rounded-full bg-ink/5 border border-[#EAEAEA] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#6B6B6B]"
              data-testid={`listing-intl-badge-${listing.id}`}
            >
              <GlobeHemisphereWest size={11} weight="duotone" className="text-gold" />
              International · {listing.seller_country} · contact to buy
            </span>
          )}
          {(listing.intl_shipping_supported || listing.breeding_rights_included) && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {listing.intl_shipping_supported && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-[#EFF7EE] border border-[#0F8043]/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#0F8043]"
                  data-testid={`listing-ships-intl-${listing.id}`}
                  title="Seller ships internationally"
                >
                  <GlobeHemisphereWest size={11} weight="duotone" />
                  Ships intl
                </span>
              )}
              {listing.breeding_rights_included && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-[#FBF7EC] border border-[#C9A227] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A6A00]"
                  data-testid={`listing-breeding-rights-${listing.id}`}
                  title="Sold with full breeding rights"
                >
                  Breeding rights
                </span>
              )}
            </div>
          )}
          {listing.color && (
            <div className="text-xs text-[#6B6B6B]">{listing.color}</div>
          )}
          {listing.breeder && (
            <div className="text-xs text-[#6B6B6B] mt-1 inline-flex items-center gap-1.5 flex-wrap">
              <Storefront size={12} weight="duotone" className="text-gold" />
              {listing.breeder.name}
              <EliteBadge elite={listing.elite_verified} showLabel={false} />
              <VerifiedBadge plan={listing.seller_plan} />
              {listing.verified_standards && <VerifiedStandardsBadge size="xs" />}
              {listing.breeder.city && <span> · {listing.breeder.city}</span>}
            </div>
          )}
          {(listing.seller_review_count || 0) > 0 && (
            <div
              data-testid={`listing-reviews-${listing.id}`}
              className="inline-flex items-center gap-1 mt-1 text-[11px] text-[#6B6B6B] w-fit font-tabular"
            >
              <Star size={11} weight="fill" className="text-gold" />
              <span className="font-bold text-ink">
                {Number(listing.seller_review_average || 0).toFixed(1)}
              </span>
              <span>· {listing.seller_review_count} {listing.seller_review_count === 1 ? "review" : "reviews"}</span>
            </div>
          )}
          <div className="gold-hairline mt-3" />
          <div className="mt-3 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6B6B6B]">
                Asking
              </div>
              <div className="font-spectral font-light text-2xl md:text-3xl tracking-tight font-tabular leading-none mt-1">
                {priceLabel(listing.asking_price, listing.sale_currency)}
              </div>
            </div>
            {listing.listed_at && (
              <div className="text-[10px] text-[#9A9A9A] font-tabular">
                Listed {formatDate(listing.listed_at)}
              </div>
            )}
          </div>
        </div>
      </Link>
      {currentUserId && currentUserId !== listing.created_by && (
        <div className="px-4 pb-4 -mt-1 space-y-2">
          <BuyNowButton listing={listing} currentUserId={currentUserId} variant="card" />
          {!(listing.seller_country && listing.seller_country !== "US") && (
            <MakeOfferButton dog={listing} currentUserId={currentUserId} variant="card" />
          )}
          <InquireButton
            dog={{
              id: listing.id,
              name: listing.name,
              created_by: listing.created_by,
              for_sale: true,
            }}
            currentUserId={currentUserId}
            variant="card"
            testIdSuffix={listing.id}
          />
        </div>
      )}
      {!currentUserId && (
        <div className="px-4 pb-4 -mt-1">
          <Link
            to={`/register?intent=buyer&next=/dogs/${listing.id}`}
            onClick={() => {
              // Persist the `next` so post-signup we land directly on
              // the listing the user cared about, not a generic dashboard.
              setPostAuthRedirect(`/dogs/${listing.id}`);
              track("signup_cta_clicked", {
                source: "marketplace_listing_card",
                listing_id: listing.id,
                breed: listing.breed,
                kennel_id: listing.breeder?.id || null,
                kennel_name: listing.breeder?.name || null,
                listing_price: listing.asking_price || null,
                is_featured: !!listing.is_featured,
              });
            }}
            className="block w-full text-center rounded-full bg-ink hover:bg-crimson text-white text-sm font-medium py-2.5 transition-colors"
            data-testid={`listing-signup-cta-${listing.id}`}
          >
            Sign up to contact breeder →
          </Link>
        </div>
      )}
    </div>
  );
}


/** Dismissible promo strip on the marketplace announcing live video
 *  tours. Hidden if the user has already dismissed it (localStorage). */
function VideoTourPromoStrip() {
  // Inline prices() call so the JSX below can never crash on a missing
  // local; see Dashboard.jsx safety-net comment.
  const { prices } = usePublicPrices();
  const KEY = "armstrong:vt_promo:dismissed:v1";
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
  });
  if (hidden) return null;
  return (
    <div
      className="mb-8 rounded-xl border border-[#E5D08A] bg-gradient-to-r from-[#FBF7EC] to-[#FDF1D0] p-4 sm:p-5 flex items-start gap-4"
      data-testid="vt-promo-strip"
    >
      <span className="hidden sm:inline-flex rounded-full bg-[#0E0E0E] text-gold w-12 h-12 items-center justify-center flex-shrink-0">
        <VideoCamera size={20} weight="duotone" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A6A00] mb-1.5 flex items-center gap-1.5">
          <Sparkle size={11} weight="fill" /> New on the registry
        </p>
        <p className="font-spectral text-[18px] sm:text-[20px] leading-tight tracking-tight text-ink">
          Meet the dog before you fly out — book a 15-minute live video tour, {prices("video_tour", "$35")}.
        </p>
        <p className="text-[12.5px] text-[#5C4A00] mt-1 leading-relaxed">
          Look for the <strong>Book video tour</strong> button on any public dog page.
          You only pay if the seller accepts; no-shows get a full refund.
        </p>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <Link
            to="/video-tours"
            className="inline-flex items-center gap-1.5 bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full px-4 h-9 text-[12.5px] font-bold transition-colors"
            data-testid="vt-promo-learn-more"
          >
            See how it works <ArrowRight size={12} weight="bold" />
          </Link>
          <button
            type="button"
            onClick={() => {
              try { localStorage.setItem(KEY, "1"); } catch {}
              setHidden(true);
            }}
            className="text-[12px] text-[#6B6B6B] hover:text-ink font-bold"
            data-testid="vt-promo-dismiss"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}


/** Horizontal carousel of adult-band listings ("1.5–7 yr"). Signals
 *  to buyers that the marketplace welcomes proven adult & retired-stud
 *  sales, not only puppies. Hidden when there are fewer than 3 adult
 *  listings so we never render an empty/awkward shelf. */
function AdultDogsShelf({ currentUserId, onJumpToAdults }) {
  const [rows, setRows] = useState(null); // null = loading
  useEffect(() => {
    api
      .get("/marketplace/listings?age=adult&sort=newest&limit=8")
      .then((r) => setRows(r.data?.listings || []))
      .catch(() => setRows([]));
  }, []);
  if (rows === null) return null;     // loading — skip render to keep above-fold quiet
  if (rows.length < 3) return null;   // not enough supply yet

  return (
    <section className="mb-10" data-testid="adult-dogs-shelf">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-1.5 flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> Adult dogs for sale
          </p>
          <h2 className="font-spectral text-xl sm:text-2xl tracking-tight leading-tight">
            Proven adults &amp; retired studs — not just puppies
          </h2>
          <p className="text-[12.5px] text-[#6B6B6B] mt-1 max-w-xl">
            Trained, titled, health-tested adults from breeders looking to place them in the right home.
          </p>
        </div>
        <button
          type="button"
          onClick={onJumpToAdults}
          className="hidden sm:inline-flex items-center gap-1 text-[12px] font-bold text-ink hover:text-crimson transition-colors whitespace-nowrap"
          data-testid="adult-shelf-see-all"
        >
          See all adults <ArrowRight size={12} weight="bold" />
        </button>
      </div>
      {/* Horizontal scroll carousel. Snap so cards always land neatly. */}
      <div
        className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory -mx-1 px-1"
        style={{ scrollbarWidth: "thin" }}
        data-testid="adult-shelf-track"
      >
        {rows.map((l) => (
          <div
            key={l.id}
            className="snap-start flex-shrink-0 w-[260px] sm:w-[280px]"
          >
            <AdultShelfCard listing={l} currentUserId={currentUserId} />
          </div>
        ))}
      </div>
    </section>
  );
}

/** Compact card for the adult sub-shelf — same visual language as the
 *  main grid `ListingCard` but with a tighter footprint and a chip
 *  highlighting computed age in years. */
function AdultShelfCard({ listing, currentUserId }) {
  const SexIcon = listing.sex === "female" ? GenderFemale : GenderMale;
  const photo = listing.photos?.[0];
  // Compute age in years for the chip. dob is ISO YYYY-MM-DD.
  const ageYears = (() => {
    if (!listing.dob) return null;
    const d = new Date(listing.dob);
    if (Number.isNaN(d.getTime())) return null;
    const diffMs = Date.now() - d.getTime();
    const yrs = diffMs / (1000 * 60 * 60 * 24 * 365.25);
    return yrs >= 1 ? Math.floor(yrs) : null;
  })();
  return (
    <Link
      to={`/dogs/${listing.id}`}
      className="block bg-white rounded-xl overflow-hidden border border-[#EAEAEA] hover:border-gold/55 hover:-translate-y-0.5 hover:shadow-gold-glow active:scale-[0.99] transition-all duration-500"
      data-testid={`adult-shelf-card-${listing.id}`}
      onClick={() => {
        if (!currentUserId) {
          setPostAuthRedirect(`/dogs/${listing.id}`);
        }
      }}
    >
      <div className="aspect-[4/3] bg-gradient-to-br from-[#FBF7EC] via-[#F3E9C7]/40 to-[#FBF7EC] border-b border-[#E2DAC2] flex items-center justify-center overflow-hidden relative">
        <HoverVideoPreview
          photoUrl={photo ? thumbUrl(photo) : null}
          videos={listing.videos}
          alt={listing.name}
          className="w-full h-full"
        />
        {ageYears !== null && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-ink/85 text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full">
            {ageYears} yr{ageYears === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <div className="p-3.5 flex flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <h3 className="font-spectral text-base tracking-tight truncate flex-1">{listing.name}</h3>
          <SexIcon size={13} weight="duotone" className={listing.sex === "female" ? "text-crimson" : "text-ink"} />
        </div>
        <div className="text-[12px] text-[#3F3F3F] truncate">{listing.breed}</div>
        <div className="flex items-end justify-between mt-1.5">
          <div className="font-spectral font-light text-lg tracking-tight font-tabular leading-none">
            {priceLabel(listing.asking_price, listing.sale_currency)}
          </div>
          {listing.breeder?.name && (
            <span className="text-[10px] text-[#9A9A9A] truncate max-w-[110px]">
              {listing.breeder.name}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * SaveSearchInline — small CTA strip below the filter sidebar.
 *
 * Renders only when a logged-in user has at least one filter set.
 * Tapping it POSTs to /api/me/saved-searches with the current filter
 * values and a label derived from those values. Subsequent visits to
 * /me/saved-searches lets them toggle alerts off or delete.
 */
function SaveSearchInline({ user, filters }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const hasAnyFilter =
    !!filters.breed || !!filters.sex || filters.min_price != null || filters.max_price != null;
  if (!user || !hasAnyFilter) return null;

  const label = [
    filters.breed,
    filters.sex && (filters.sex === "male" ? "Male" : "Female"),
    filters.max_price ? `≤ $${Number(filters.max_price).toLocaleString()}` : null,
  ].filter(Boolean).join(" · ") || "Saved search";

  const onSave = async () => {
    setSaving(true);
    try {
      // Strip empty/null values before POST. The backend rejects
      // `sex:""` against its `^(male|female)$` regex (Pydantic treats
      // `Optional[str]` + non-empty pattern as: either None or matches).
      // The default UI state for unset filters is "" or null, so we
      // defensively drop falsy fields here instead of letting them
      // through as 422s. (Backend was hardened to also coerce '' → None
      // in 2026-05-31.)
      const cleaned = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v != null),
      );
      await api.post("/me/saved-searches", { label, alerts_enabled: true, ...cleaned });
      setSaved(true);
      toast.success("Saved — we'll email you daily when new ones match.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div className="pt-3 border-t border-[#F0F0F0] text-center" data-testid="market-savesearch-saved">
        <p className="text-[11.5px] text-[#3DA56E] font-bold mb-1">
          <BellRinging size={11} weight="fill" className="inline mr-1" />
          Daily alerts on
        </p>
        <Link to="/me/saved-searches" className="text-[11px] text-[#6B6B6B] hover:text-crimson underline">
          Manage saved searches →
        </Link>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onSave}
      disabled={saving}
      data-testid="market-savesearch-cta"
      className="w-full pt-3 border-t border-[#F0F0F0] text-center text-[12px] text-ink hover:text-crimson font-bold flex items-center justify-center gap-1.5 disabled:opacity-60"
    >
      <BellRinging size={13} weight="duotone" />
      {saving ? "Saving…" : "Save this search & alert me"}
    </button>
  );
}
